import React from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { Grid, Header, Segment, Button, Icon, Checkbox } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import InfoPopup from '~/components/InfoPopup'
import TreeSpecies from './TreeSpecies'
import CropPriorChanges from './CropPriorChanges'

const Carbon: React.FC = () => {
  const { t } = useTranslation()
  const { control, watch, setValue } = useFormContext()

  const hasCarbonChanges = watch('hasCarbonChanges')

  const { fields: treeFields, append: appendTree, remove: removeTree } = useFieldArray({
    control,
    name: 'treeTypes'
  })

  function renderTreeSpeciesHeader(index) {
    return <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
      <Header as='h4'>{t('Tree Species')} {index + 1}</Header>
      <Icon
        name='trash'
        color='red'
        size='large'
        style={{ cursor: 'pointer' }}
        onClick={() => removeTree(index)}
      />
    </div>
  }

  function renderTreeMainHeader() {
    return <Header attached='top' block>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        {t('Out of crop biomass changes')}
        <InfoPopup
          style={{ minWidth: 400 }}
          content={t('Add annual changes for the biomass of trees growing within or immediately adjacent to the field assessment area. Ensure these are not counted in adjoining crop assessments to prevent double counting. As per GHG Protocol, you should only account for increased biomass if the biomass is permanent and additional. Trees or orchards that have been there > 20 years or the lifecycle of the perennial crop should not be included. If unsure, be conservative and do not include.')}
          styleIcon={{ fontSize: '1.3em' }}
        />
      </div>
    </Header>
  }

  function renderCropChangesHeader() {
    return <Header attached='top' block>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        {t('In Crop carbon changes')}
        <InfoPopup
          style={{ minWidth: 400 }}
          content={t(`Tick the relevant boxes below if you have made changes to land use, tillage, or carbon inputs in your assessment area during the last 20 years.
            We appreciate that many crops are grown in rotation. As such, this guidance is for the field over the last twenty years - but the practices must be undertaken at least every three years to qualify.
            Enter changes starting from the most recent intervention, working backwards to the first intervention undertaken.
            The 'From' state of your oldest change is your baseline (i.e. the state prior to your first intervention).`)}
          styleIcon={{ fontSize: '1.3em' }}
        />
      </div>
    </Header>
  }

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={16}>
          {renderCropChangesHeader()}
          <Segment attached>
            <div style={{ marginBottom: '1em' }}>
              <Checkbox
                label={t('Has any part of the field management practice changed between tillage, land use or inputs in the last 20 years?')}
                checked={hasCarbonChanges || false}
                onChange={(e, { checked }) => setValue('hasCarbonChanges', checked)}
              />
            </div>

            {hasCarbonChanges && <CropPriorChanges />}
          </Segment>
        </Grid.Column>
      </Grid.Row>
      
      <Grid.Row>
        <Grid.Column width={16}>
          {renderTreeMainHeader()}
          <Segment attached>
            {treeFields.map((field, index) => (
              <Segment key={field.id} style={{ marginBottom: '1em' }}>
                {renderTreeSpeciesHeader(index)}
                <TreeSpecies index={index} />
              </Segment>
            ))}
            <div style={{ display: 'flex', justifyContent: 'end', marginTop: '1em' }}>
              <Button
                type='button'
                icon='plus'
                style={{ color: 'white' }}
                content={t('Add Tree Species')}
                onClick={() => appendTree({
                  id: null,
                  densityLastYear: null,
                  sizeLastYear: null,
                  sizeThisYear: null,
                  treeBalance: null
                })}
              />
            </div>
          </Segment>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

export default Carbon
